// Heuristic parser: turns the backend's free-text `response` into structured
// pieces the report template can render (comparison table, expected/found
// box, short reason) instead of showing raw text. Falls back gracefully —
// the original explanation is always preserved and shown regardless.

const DOC_TOKEN = /^(?:Form|Document|File)\s*\d+$/i;
const isDocToken = (t) => DOC_TOKEN.test(t.trim());
const getQuoted = (text) => (text.match(/'([^']+)'/g) || []).map((s) => s.slice(1, -1));

const extractDocValuePairs = (text) => {
  const pairs = [];
  const re =
    /((?:'(?:Form|Document|File)\s*\d+'\s*(?:,\s*|and\s+)?)+)(?:both\s+)?(?:has|have)\s+(?:the\s+)?value\s+'([^']+)'/gi;
  let m;
  while ((m = re.exec(text))) {
    const docs = (m[1].match(/'((?:Form|Document|File)\s*\d+)'/g) || []).map((s) => s.slice(1, -1));
    docs.forEach((doc) => pairs.push({ doc, value: m[2] }));
  }
  return pairs;
};

const extractInsteadOf = (text) => {
  const m = text.match(/\(?\s*([^()]+?)\s+instead\s+of\s+([^()]+?)\s*\)?(?:[.,]|$)/i);
  return m ? { found: m[1].trim(), expected: m[2].trim() } : null;
};

const extractMatchGroup = (text) => {
  const m = text.match(
    /((?:'(?:Form|Document|File)\s*\d+'\s*(?:,\s*|and\s+)?)+)\s*(?:have|match)(?:es)?\s+(?:the\s+)?(?:same\s+value|exactly|each other)/i
  );
  return m
    ? (m[1].match(/'((?:Form|Document|File)\s*\d+)'/g) || []).map((s) => s.slice(1, -1))
    : [];
};

const extractReason = (text) => {
  const patterns = [
    /(?:has a different|different)\s+([a-z0-9\s]+?)\s*\(/i,
    /(?:it lacks|lacks)\s+([a-z0-9\s]+?)\s*\(/i,
  ];
  for (const p of patterns) {
    const m = text.match(p);
    if (m) {
      const clause = m[1].trim().replace(/^the\s+/i, '');
      return clause.charAt(0).toUpperCase() + clause.slice(1) + ' mismatch.';
    }
  }
  if (/includes additional text/i.test(text)) return 'Contains extra, unexpected text.';
  if (/completely different/i.test(text)) return 'Values do not match across documents.';
  if (/match exactly|same value/i.test(text) && /but|however/i.test(text))
    return 'One document deviates from the rest.';
  return 'Value mismatch detected between documents.';
};

const summaryForScore = (score) => {
  if (score >= 90) return 'Values match across all documents.';
  if (score >= 40) return 'Partial match detected.';
  if (score > 0) return 'Significant mismatch detected.';
  return 'No match found across documents.';
};

const statusForScore = (score) => {
  if (score >= 90) return { label: 'Matched', tone: 'success' };
  if (score >= 40) return { label: 'Partial Match', tone: 'warning' };
  return { label: 'Failed', tone: 'danger' };
};

const confidenceForScore = (score) => (score >= 90 ? 'High' : score >= 40 ? 'Medium' : 'Low');

export const parseAnalysisItem = (item) => {
  const { score = 0, response = '' } = item;
  const docValuePairs = extractDocValuePairs(response);
  const matchGroup = extractMatchGroup(response);
  const insteadOf = extractInsteadOf(response);

  let comparisons = [];
  if (docValuePairs.length >= 2) {
    const values = docValuePairs.map((p) => p.value);
    const counts = values.reduce((acc, v) => ((acc[v] = (acc[v] || 0) + 1), acc), {});
    const majority = Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0];
    comparisons = docValuePairs.map((p) => ({
      document: p.doc,
      value: p.value,
      status: p.value === majority ? 'Match' : 'Different',
    }));
  } else if (matchGroup.length >= 2 && insteadOf) {
    const quoted = getQuoted(response).filter(isDocToken);
    const oddOneOut = quoted.find((d) => !matchGroup.includes(d));
    matchGroup.forEach((doc) =>
      comparisons.push({ document: doc, value: insteadOf.expected, status: 'Match' })
    );
    if (oddOneOut)
      comparisons.push({ document: oddOneOut, value: insteadOf.found, status: 'Different' });
  }

  return {
    score,
    summary: summaryForScore(score),
    explanation: response,
    status: statusForScore(score),
    confidence: confidenceForScore(score),
    comparisons,
    expectedFound: insteadOf,
    reason: insteadOf || comparisons.length ? extractReason(response) : null,
  };
};

export default parseAnalysisItem;
